import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { FiltersProvider } from '@Sage/ssm-web5';
import { Text, Spacer } from '@Sage/hoi-poi-ui';
import { CONTACTS } from 'constants/Entities';
import { EntityListSelectActions } from 'actions';
import { MarketingStatusCell } from 'components/TableCells';
import FiltersLayout from 'containers/components/layouts/FiltersLayout';
import EntityMenu from 'containers/components/EntityMenu';
import NewEntityList from 'containers/components/NewEntityList';
import SearchContacts from 'containers/contacts/components/SearchContacts';
import { getLiteral } from 'utils/getLiteral';
import CONFIG from './ContactsTableConfig';

const CACHE_ID = 'subscribers_contacts_list';

function mapStateToProps(state) {
    const selection = state.entityListSelect[CONTACTS.entity];
    const selected = selection?.selected || {};
    const includedIds = Object.keys(selected).filter((id) => selected[id] === true);

    return {
        bulkSelectedEntities: selection?.total ?? 0,
        selectedIds: includedIds,
        shouldClearWeb5Checkboxes: selection?.shouldClearWeb5Checkboxes,
        spamchimp: state?.config?.flagsmith?.spamchimp,
        canEdit: !!state?.config?.permission?.crud_permission?.[CONTACTS.permission]?.update,
    };
}

const mapDispatchToProps = (dispatch) => ({
    onEntitySelect: bindActionCreators(EntityListSelectActions, dispatch).onEntitySelect,
    onEntitySelectAll: bindActionCreators(EntityListSelectActions, dispatch).onEntitySelectAll,
    setShouldClearWeb5Checkboxes: bindActionCreators(EntityListSelectActions, dispatch)
        .setShouldClearWeb5Checkboxes,
});

const ContactsTab = memo(
    ({
        bulkSelectedEntities,
        selectedIds,
        shouldClearWeb5Checkboxes,
        spamchimp,
        canEdit,
        onEntitySelect,
        onEntitySelectAll,
        setShouldClearWeb5Checkboxes,
        onRequestBulkSubscribedConfirm,
    }) => {
        const tableRef = useRef(null);

        useEffect(() => {
            if (shouldClearWeb5Checkboxes) {
                if (tableRef?.current?.clearCheckboxes) tableRef.current.clearCheckboxes();
                setShouldClearWeb5Checkboxes(CONTACTS, false);
            }
        }, [setShouldClearWeb5Checkboxes, shouldClearWeb5Checkboxes]);

        const handleOnRef = useCallback((ref) => {
            tableRef.current = ref;
        }, []);

        const onClickCheckbox = useCallback(
            ({ id, isColumn, value, checked, data, rowIndex, total }) => {
                if (isColumn) {
                    onEntitySelectAll(CONTACTS, value, checked, total);
                } else {
                    onEntitySelect(CONTACTS, id, value, data, rowIndex, checked);
                }
            },
            [onEntitySelect, onEntitySelectAll],
        );

        const config = useMemo(
            () => CONFIG({ onClickCheckbox, spamchimp, canEdit }),
            [onClickCheckbox, spamchimp, canEdit],
        );

        const bulkOperationsLiterals = useMemo(
            () => ({
                labelTitleMenuTotalSelected: 'label_num_contacts_selected',
                labelTitleMenuTotalSelectedSingular: 'label_num_contacts_selected_singular',
                entityNamePlural: 'label_contacts',
            }),
            [],
        );

        const extraBulkActions = useMemo(
            () => [
                {
                    icon: 'verified',
                    tooltip: getLiteral('action_mark_as_subscribed_contacts'),
                    onClick: () =>
                        onRequestBulkSubscribedConfirm?.({
                            entity: CONTACTS,
                            filterKey: config?.filterKey,
                            selectedIds,
                            title: 'title_marketing_campaigns_mark_contacts_as_subscribers_question',
                            introLiteral:
                                'label_marketing_campaigns_bulk_subscribe_confirm_intro_contacts',
                            introParams: [bulkSelectedEntities],
                            bodyLiterals: [
                                'label_marketing_campaigns_bulk_subscribe_eligible',
                                'label_marketing_campaigns_bulk_subscribe_immediate',
                            ],
                            confirmLiteral: 'action_mark_as_subscribed_contacts',
                        }),
                    isDisabled: bulkSelectedEntities === 0 || !config?.filterKey,
                    className: 'bulk-operation-give-consent',
                },
            ],
            [bulkSelectedEntities, config?.filterKey, onRequestBulkSubscribedConfirm, selectedIds],
        );

        if (!config) return null;

        return (
            <FiltersProvider>
                <FiltersLayout config={config}>
                    <Text type="h6" className="fm-subscribers-tab__title">
                        {getLiteral('label_contacts')}
                    </Text>
                    <Spacer y={2} />
                    <EntityMenu
                        entity={CONTACTS}
                        filterKey={config.filterKey}
                        withFilters={true}
                        quickFilters={config.quickFilters}
                        useQuickView={config.useQuickView}
                        searchComponent={<SearchContacts filterKey={config.filterKey} />}
                        withBulkOperations={true}
                        bulkSelectedEntities={bulkSelectedEntities}
                        bulkOperationsLiterals={bulkOperationsLiterals}
                        extraBulkActions={extraBulkActions}
                        hideDefaultActions={true}
                        canMassiveUpdate={false}
                        canMassiveDelete={false}
                        canMerge={false}
                    />
                    <NewEntityList
                        id="fm-grid-subscribers-contacts"
                        entity={CONTACTS}
                        config={config}
                        cacheId={CACHE_ID}
                        filterKey={config.filterKey}
                        initOnReady={true}
                        useLazyLoad={true}
                        useCache={true}
                        useSort={true}
                        useFieldSelector={false}
                        discardCount={true}
                        domLayout="normal"
                        components={{ MarketingStatusCell }}
                        handleOnRef={handleOnRef}
                    />
                </FiltersLayout>
            </FiltersProvider>
        );
    },
);

export default connect(mapStateToProps, mapDispatchToProps)(ContactsTab);
